import React, { Dispatch, SetStateAction, useEffect } from 'react'

import { Fade, Modal, Grid } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from 'providers/AuthProvider'
import Backdrop from '@mui/material/Backdrop'
import CustomButton from 'components/Button/CustomButton'
import { Add } from '@mui/icons-material'
import BenefitsBlock from 'components/DataDisplay/BenefitsBlock'
import { Toggle5 } from 'components/StateMod/Toggle'
import { AnimateSharedLayout } from 'framer-motion'
import { useWallets } from 'providers/WalletsProvider'
import blocto from '/assets/Icon/blocto.svg'
import flowWalletIcon from '/assets/Icon/flowWallet.png'
import magic from '/assets/Icon/Logo-Magic.svg'
import CollectibleTransferCard from 'components/Card/CollectibleTransferCard'
import Spinner from 'components/Transactions/Spinner'
import FaqSection from 'components/ContentBlock/FaqSection'

export interface ComingSoonModalProps {
  isMagic: boolean
  setupWalletModalOpen: boolean
  setSetupWalletModalOpen: Dispatch<SetStateAction<boolean>>
}

const SetupWalletModal = ({
  isMagic,
  setupWalletModalOpen,
  setSetupWalletModalOpen
}: ComingSoonModalProps) => {
  const navigate = useNavigate()
  const {
    isConnected,
    connectMagic,
    disconnect,
    registeredWallets,
    connectedWalletAddress,
    magicMomentCount,
    usingMagic,
    magicWalletAddress,
    magicWalletWUSDCBalance,
    magicForSaleMomentCount,
    magicNotForSaleMomentCount
  } = useWallets()
  // Blocto removed — local empty defaults so the legacy "Flow Wallet" card
  // in this dead-but-still-bundled modal still renders without crashing.
  const bloctoMomentCount = 0
  const bloctoWalletAddress = ''
  const bloctoWalletWUSDCBalance = 0
  const bloctoForSaleMomentCount = 0
  const bloctoNotForSaleMomentCount = 0
  const [activeStep, setActiveStep] = React.useState(0)
  const [creatingWallet, setCreatingWallet] = React.useState(false)
  const _isConnected = isMagic
    ? usingMagic && isConnected
    : !usingMagic && isConnected
  const handleClose = () => {
    setSetupWalletModalOpen(false)
  }
  const handleConnect = () => {
    if (isMagic) {
      setCreatingWallet(true)
      handleMagicConnect()
    } else {
      handleBloctoConnect()
    }
    handleClose()
  }

  const handleMagicConnect = () => {
    if (!_isConnected) {
      connectMagic().then(() => {
        setActiveStep(1)
        setCreatingWallet(false)
      })
    } else {
      disconnect()
    }
  }

  const handleBloctoConnect = () => {
    // Blocto removed — legacy code path retained as no-op so the modal still
    // renders for any deep-link that hits this branch.
    if (!_isConnected) {
      setActiveStep(1)
    } else {
      disconnect()
    }
  }

  const activeMagicWallet = [
    {
      name: 'Magic',
      icon: magic,
      address: magicWalletAddress,
      balance: magicWalletWUSDCBalance,
      collectibles: magicMomentCount,
      forSaleCollectibles: magicForSaleMomentCount,
      notForSaleCollectibles: magicNotForSaleMomentCount
    }
  ]
  const activeBloctoWallet = [
    {
      name: 'Flow/Blocto',
      icon: [flowWalletIcon, blocto],
      address: bloctoWalletAddress,
      balance: bloctoWalletWUSDCBalance,
      collectibles: bloctoMomentCount,
      forSaleCollectibles: bloctoForSaleMomentCount,
      notForSaleCollectibles: bloctoNotForSaleMomentCount
    }
  ]

  const AddWallet = () => {
    return (
      <div className="flex w-full max-w-screen-sm flex-col items-center justify-center gap-6">
        <div className="pt-4 text-center text-white md:pt-0">
          <p className="pb-2 font-podium49 text-4xl uppercase md:text-5xl">
            <span className="hidden md:inline-block">Get your free</span>
            {isMagic ? ' Magic wallet' : ' wallet'}
          </p>
          <p className="text-xl md:text-2xl">
            {isMagic
              ? 'A new wallet option for faster login and transactions'
              : 'Choose a wallet to get started'}
          </p>
        </div>
        <div className="flex flex-col gap-3 py-1">
          <BenefitsBlock
            content={
              isMagic
                ? 'Automatically connects with AFL iD login when your collection is in Magic.'
                : 'Free to setup'
            }
          />
          <BenefitsBlock
            content={
              isMagic
                ? 'No extra email codes to enter to connect'
                : 'Easy to use – no crypto knowledge needed'
            }
          />
          <BenefitsBlock
            content={
              isMagic
                ? 'Easily transfer your collection to Magic'
                : 'Securely store your AFL digital collectibles'
            }
          />
        </div>
        <AnimateSharedLayout>
          <Toggle5 title="Good to know" width="100%">
            {isMagic ? (
              <div className="answer !pt-[14px]">
                <p className="mb-4 text-xs text-grey3">
                  When you create an account a ‘Magic’ digital wallet will be
                  created for you to use on AFL Mint. This will be created using
                  the email address provided with your AFL iD and does not
                  required a separate password or login to use. If you have an
                  existing Magic wallet from another project it is not possible
                  to import your previous Magic wallet at this time. A digital
                  wallet holds proof of digital tokens like cryptocurrencies and
                  digital collectibles and is required to participate in the AFL
                  Mint program. AFL Mint uses{' '}
                  <a
                    href="https://magic.link/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:no-underline"
                  >
                    Magic wallet
                  </a>{' '}
                  as the primary wallet. After signing up other wallet options
                  are available.
                </p>
                <p className="text-xs italic text-grey3">
                  Australian Football League (<strong>AFL</strong>) does not
                  provide crypto or digital wallets (
                  <strong>Digital Wallet</strong>) or any services in respect
                  of, or access to, any Digital Wallets. Any Digital Wallet
                  which can be accessed from any AFL website or otherwise used
                  in connection with any officially licensed AFL digital
                  collectibles is provided by a third party. Before using or
                  accessing any Digital Wallet or agreeing to deposit or store
                  any officially licensed AFL Digital Collectibles into a
                  Digital Wallet, you should read and consider any terms and
                  conditions which apply to the use of, and access to, the
                  Digital Wallet. To the extent permitted by law, AFL is not
                  responsible or liable for any loss or damage which may be
                  suffered or incurred by you or any other person in respect of
                  your or that person’s use of, or access to, any Digital
                  Wallet, including any loss or damage to any officially
                  licensed AFL Digital Collectible as a result of using the
                  Digital Wallet or depositing or storing the officially
                  licensed AFL Digital Collectible in the Digital Wallet.
                </p>
              </div>
            ) : (
              <div className="answer !pt-[14px]">
                <p className="mb-4 text-sm text-grey3">
                  A digital wallet holds proof of digital tokens like
                  cryptocurrencies and NFTs and is required to participate in
                  the AFL Mint program.
                </p>
                <p className="text-xs italic text-grey3">
                  Australian Football League (<strong>AFL</strong>) does not
                  provide crypto or digital wallets (
                  <strong>Digital Wallet</strong>) or any services in respect
                  of, or access to, any Digital Wallets. Any Digital Wallet
                  which can be accessed from any AFL website or otherwise used
                  in connection with any officially licensed AFL digital
                  collectibles is provided by a third party. Before using or
                  accessing any Digital Wallet or agreeing to deposit or store
                  any officially licensed AFL Digital Collectibles into a
                  Digital Wallet, you should read and consider any terms and
                  conditions which apply to the use of, and access to, the
                  Digital Wallet. To the extent permitted by law, AFL is not
                  responsible or liable for any loss or damage which may be
                  suffered or incurred by you or any other person in respect of
                  your or that person’s use of, or access to, any Digital
                  Wallet, including any loss or damage to any officially
                  licensed AFL Digital Collectible as a result of using the
                  Digital Wallet or depositing or storing the officially
                  licensed AFL Digital Collectible in the Digital Wallet.
                </p>
              </div>
            )}
          </Toggle5>
        </AnimateSharedLayout>
        <div className="mt-[-10px] flex max-w-screen-xsm flex-col gap-4">
          <CustomButton
            label={isMagic ? 'Add Magic wallet' : 'Add wallet'}
            model="primary"
            variant="contained"
            onClick={handleConnect}
            disabled={creatingWallet}
          />
          {creatingWallet && (
            <div className="flex flex-col items-center gap-6 text-white">
              <Spinner />
              Creating wallet in process, please wait
            </div>
          )}
          <CustomButton
            label="Cancel"
            model="secondary"
            variant="contained"
            onClick={() => handleClose()}
          />
          <div>
            {isMagic && (
              <p className="text-center text-xs text-grey3">
                By clicking ‘Create account’ you agree to create a ‘Magic’
                digital wallet as part of your AFL Mint account to securely
                store any collectibles and credits you choose to use on AFL
                Mint. See Magic{' '}
                <a
                  href="https://magic.link/legal/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:no-underline"
                >
                  terms of service
                </a>{' '}
                and{' '}
                <a
                  href="https://magic.link/legal/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:no-underline"
                >
                  privacy policy
                </a>
                .
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  const WalletAdded = () => {
    return (
      <div className="flex w-full max-w-screen-sm flex-col items-center justify-center gap-6">
        <div className="flex w-full max-w-screen-sm flex-col items-center justify-center gap-2 pt-4 text-center text-white md:pt-0">
          <p className="font-podium49 text-4xl uppercase md:text-5xl">
            Wallet added
          </p>
          <p className="md:text-lg">
            You can now transfer your collection to your new wallet
          </p>
        </div>
        <div className="flex w-full max-w-screen-xsm gap-3 text-white">
          <CollectibleTransferCard
            wallets={usingMagic ? activeMagicWallet : activeBloctoWallet}
          />
        </div>
        <div className="flex w-full max-w-screen-xsm flex-col items-center justify-center gap-4">
          <Link
            to="/account?section=wallet-transfer"
            className="w-full underline hover:no-underline"
          >
            <CustomButton
              label="Continue to transfer"
              model="primary"
              variant="contained"
              hasEndIcon={true}
              className="w-full"
            />
          </Link>
          <CustomButton
            label="Maybe later"
            model="secondary"
            variant="contained"
            className="w-full"
            onClick={() => setActiveStep(2)}
          />
        </div>
        <div className="w-full text-center text-grey3">
          Manage your wallets at any time from your{' '}
          <Link
            to="/account?section=manage-wallets"
            className="underline hover:no-underline"
          >
            account settings
          </Link>
        </div>
      </div>
    )
  }

  const LaterConfirmation = () => {
    return (
      <div className="flex w-full max-w-screen-sm flex-col items-center justify-center gap-6">
        <div className="flex w-full max-w-screen-sm flex-col items-center justify-center gap-2 pt-4 text-center text-white md:pt-0">
          <p className="font-podium49 text-4xl uppercase md:text-5xl">Got it</p>
          <p>
            You can manage your wallets and transfer your collection from your{' '}
            <Link
              to="/account?section=manage-wallets"
              className="underline hover:no-underline"
            >
              account settings
            </Link>{' '}
            at any time.
          </p>
        </div>
        <Link to="/account" className="flex flex-col-reverse gap-4 md:flex-col">
          <CustomButton
            label="Back to account"
            model="primary"
            variant="contained"
          />
        </Link>
      </div>
    )
  }

  return (
    <Modal
      open={setupWalletModalOpen}
      onClose={setSetupWalletModalOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: 'transparent'
        }
      }}
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: 'transparent'
        }
      }}
      className="h-full overflow-auto bg-black p-10 md:p-20"
    >
      <div className="absolute left-1/2 flex w-full translate-x-[-50%] flex-col items-center px-4">
        <div
          className="absolute right-6 top-0 flex w-full cursor-pointer items-end justify-end md:right-10 md:top-0"
          onClick={() => handleClose()}
        >
          <Add
            style={{
              transform: `rotate(45deg)`,
              color: '#ffffff',
              fontSize: '40px'
            }}
          />
        </div>
        <div className="flex w-full max-w-screen-md flex-col items-center justify-center bg-card p-4 sm:p-8 lg:p-12">
          {activeStep === 0 && <AddWallet />}
          {activeStep === 1 && <WalletAdded />}
          {activeStep === 2 && <LaterConfirmation />}
        </div>
        {/* <div className="text-white w-full">
          <FaqSection
            header={isMagic ? 'About Magic' : 'About Blocto'}
            faqName={isMagic ? 'magicWallet' : 'bloctoWallet'}
          />
        </div> */}
      </div>
    </Modal>
  )
}

export default SetupWalletModal
